import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import initParticles from '../constants/particles.js';
import './TokenShop.css';

// Initialize Stripe with your publishable key
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripeKey) {
    console.error('Stripe Publishable Key is missing. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env');
}
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const TokenShop = () => {
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const canvasRef = useRef(null);
    localStorage.setItem("redirectAfterLogin", window.location.pathname);

    // Token Management
    const decodeToken = (token) => {
        try {
            return JSON.parse(atob(token.split(".")[1]));
        } catch (err) {
            console.error("Invalid token format:", err);
            return null;
        }
    };

    const verifyToken = (token) => {
        const decoded = decodeToken(token);
        if (!decoded) return false;
        const currentTime = Math.floor(Date.now() / 1000);
        return decoded.exp > currentTime;
    };

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        window.location.href = '/login';
    };

    const fetchAccessTokenIfNeeded = async () => {
        const token = localStorage.getItem("token");
        if (!token) return null;

        if (!verifyToken(token)) {
            try {
                const refreshToken = localStorage.getItem("refreshToken");
                const response = await axios.post("/api/auth/refresh-token", { refreshToken });
                const { accessToken } = response.data;
                localStorage.setItem("token", accessToken);
                return accessToken;
            } catch (err) {
                console.error("Failed to refresh token:", err);
                handleLogout();
                return null;
            }
        }
        return token;
    };

    // Fetch products from backend on mount
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await fetch('/api/auth/tokens/products');
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || 'Failed to fetch products.');
                }
                setProducts(data.products);
            } catch (err) {
                setError('Failed to load products. Please try again later.');
                console.error(err);
            }
        };
        fetchProducts();
    }, []);

    // Initialize particles
    useEffect(() => {
        if (canvasRef.current) {
            const cleanup = initParticles(canvasRef.current);
            return cleanup;
        }
    }, []);

    const handlePurchase = async () => {
        if (!selectedProduct) {
            setError('Please select a product.');
            return;
        }
        if (!selectedProduct.selectedPrice) {
            setError('Selected product is missing price information.');
            return;
        }
        if (!stripePromise) {
            setError('Stripe is not configured. Please contact support.');
            return;
        }

        // Retrieve or refresh the access token
        const authToken = await fetchAccessTokenIfNeeded();
        if (!authToken) {
            setError('You must be logged in to make a purchase. Please log in and try again.');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await fetch('/api/auth/tokens/checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    durationDays: selectedProduct.durationDays,
                    description: selectedProduct.description,
                    priceId: selectedProduct.selectedPrice.id,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to initiate checkout.');
            }

            const stripe = await stripePromise;
            const { error: stripeError } = await stripe.redirectToCheckout({
                sessionId: data.id,
            });

            if (stripeError) {
                throw new Error(stripeError.message);
            }
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    return (
        <div className="token-shop-container">
            <div className="background-animation-wrapper">
                <canvas ref={canvasRef} className="particle-canvas"></canvas>
            </div>
            <div className="token-shop">
                <h1 className="shop-title">Purchase a Token</h1>
                <p className="shop-description">
                    Select a token to unlock premium features on Sintrix.net.
                </p>

                <div className="product-list">
                    {products.length === 0 && !error && (
                        <div className="loading-spinner">Loading products...</div>
                    )}
                    {products.map((product) => (
                        <div
                            key={product.id}
                            className={`product-card ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                            onClick={() => setSelectedProduct({ ...product, selectedPrice: product.prices[0] })}
                        >
                            <h3>{product.name}</h3>
                            <p className="product-description">{product.description}</p>
                            {product.prices.length > 0 ? (
                                <p className="price">${product.prices[0].unit_amount.toFixed(2)}</p>
                            ) : (
                                <p className="price">Price not available</p>
                            )}
                        </div>
                    ))}
                </div>

                {error && <p className="error-message">{error}</p>}

                <button
                    className="purchase-button"
                    onClick={handlePurchase}
                    disabled={loading || !selectedProduct}
                >
                    {loading ? (
                        <span className="spinner"></span>
                    ) : (
                        'Buy Now'
                    )}
                </button>
            </div>
        </div>
    );
};

export default TokenShop;